import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AlertTriangle, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/state/AppContext";
import { usePageTitle } from "@/lib/usePageTitle";
import { spacedPhone } from "@/lib/format";
import { api, apiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ProfileForm } from "@/components/ProfileForm";
import { OtpInput } from "@/components/OtpInput";
import { GoogleButton } from "@/components/GoogleButton";
import { AsLogo } from "@/components/AsLogo";
import { roleHome } from "@/lib/roleHome";
import { safeNext, stripCity } from "@/lib/loginPath";

// Seconds before "send a new code" re-enables (UAT L11). Matches the server's per-phone OTP pacing
// closely enough that a tap never just earns a rate-limit toast.
const RESEND_SECONDS = 30;

// `initialStep` lets the dedicated /staff-login route open the admin/staff sign-in directly
// (Google staff sign-in only — the password form went with `POST /auth/admin/login` in Phase F).
// The public consumer route (/login) defaults to "phone" and
// carries no discoverable path into the admin step — see D-10.
export default function Login({ initialStep = "phone" }) {
  const { t, tenant, theme, auth, setSession } = useApp();
  usePageTitle(t("common.signIn")); // R4-7: per-page tab title
  const navigate = useNavigate();
  const location = useLocation();
  // Where a guard / sign-in prompt asked to return to (UAT L1). Validated: in-app paths only.
  const next = stripCity(safeNext(new URLSearchParams(location?.search || "").get("next")), tenant?.slug);
  const [cooldown, setCooldown] = useState(0);
  // U13: a guard / sign-in prompt sends people here from wherever they had scrolled to; the sign-in
  // form is at the top of this page, so start there instead of mid-page.
  useEffect(() => {
    if (typeof window !== "undefined" && window.scrollTo) window.scrollTo(0, 0);
  }, []);
  // RF-23: an already-signed-in person on the staff sign-in has nothing to do here - send them to
  // their role's home (or where a guard asked to return to) instead of showing the form again.
  const signedInRole = auth?.access_token ? auth.active_role_type || "" : null;
  useEffect(() => {
    if (initialStep !== "admin" || signedInRole === null) return;
    navigate(next || roleHome[signedInRole] || "/platform", { replace: true });
  }, [initialStep, signedInRole, next, navigate]);
  const [step, setStep] = useState(initialStep);
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [referralCode, setReferralCode] = useState("");
  const [code, setCode] = useState("");
  const [devCode, setDevCode] = useState("");
  const [requestId, setRequestId] = useState("");
  const [normalizedPhone, setNormalizedPhone] = useState("");
  const [landing, setLanding] = useState("/account");
  const [loading, setLoading] = useState(false);
  const [otpError, setOtpError] = useState("");

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const timer = setTimeout(() => setCooldown((n) => n - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const sendOtp = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/otp/request", { phone, tenant_id: tenant?.id });
      setRequestId(data.request_id);
      setNormalizedPhone(data.phone);
      setOtpError("");
      // Present only while DEV_OTP_MODE is on (SMS cannot be delivered until TRAI DLT approves
      // our templates). With the flag off the field is absent, this stays "", and the OTP step
      // renders the ordinary "we sent you an SMS" path with nothing extra on it.
      setDevCode(data.otp_bypass_code || "");
      setCooldown(RESEND_SECONDS);
      setStep("otp");
    } catch (e) {
      toast.error(apiErrorMessage(e, t("errors.generic")));
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async () => {
    setLoading(true);
    setOtpError("");
    try {
      const { data } = await api.post("/auth/otp/verify", {
        request_id: requestId,
        code,
        tenant_id: tenant?.id,
        display_name: name || undefined,
        referral_code: referralCode || undefined,
      });
      setSession(data);
      toast.success(t("auth.success"));
      // The page the person came from beats their role's home; a guard bounces them onward if
      // their role cannot open it.
      const home = next || roleHome[data.active_role_type] || "/account";
      setLanding(home);
      if (data.identity?.profile_completed) navigate(home);
      else setStep("profile");
    } catch (e) {
      // Any failure is shown inline at the field. A rejected code (4xx other than rate-limit) gets
      // the fixed wrong-or-expired wording; anything else (429, 5xx, network) keeps its own message.
      const status = e?.response?.status;
      const rejected = status >= 400 && status < 500 && status !== 429;
      setOtpError(rejected ? t("auth.otpWrong") : apiErrorMessage(e, t("errors.generic")));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto flex max-w-md flex-col px-5 py-16 sm:px-0">
      <AsLogo size={48} className="text-[var(--as-primary)]" title="Apna Shahar" />
      <h1 className="mt-6 font-heading text-3xl font-bold tracking-tight">
        {/* On the admin host there is no city theme/tenant, so `app` was empty and the heading read
            "Sign in to " with a dangling preposition. Fall back to the platform mark. */}
        {t("auth.title", { app: theme?.app_name || tenant?.name || "Apna Shahar" })}
      </h1>

      {step === "admin" ? (
        <div className="mt-6 space-y-5" data-testid="login-admin-step">
          <h2 className="font-heading text-xl font-semibold">{t("auth.adminTitle")}</h2>
          <p className="text-sm text-muted-foreground">{t("auth.adminSubtitleGoogle")}</p>
          <GoogleButton label={t("auth.googleStaffSignIn")} testId="google-staff-signin-btn" />
          <p className="text-xs text-muted-foreground">{t("auth.googleStaffHint")}</p>
          {/* RF-14: staff sign in with Google only - no phone-OTP fallback on the staff sign-in. */}
        </div>
      ) : step === "profile" ? (
        <div className="mt-6 space-y-5" data-testid="login-profile-step">
          <h2 className="font-heading text-xl font-semibold">{t("profile.setupTitle")}</h2>
          <p className="text-sm text-muted-foreground">{t("profile.setupSubtitle")}</p>
          <ProfileForm onSaved={() => navigate(landing)} onSkip={() => navigate(landing)} />
        </div>
      ) : step === "phone" ? (
        <div className="mt-6 space-y-5" data-testid="login-phone-step">
          <p className="text-sm text-muted-foreground">{t("auth.subtitle")}</p>
          <div className="space-y-2">
            <Label htmlFor="phone">{t("auth.phoneLabel")}</Label>
            <Input
              id="phone"
              data-testid="login-phone-input"
              value={phone}
              type="tel"
              inputMode="numeric"
              autoComplete="tel"
              onChange={(e) => setPhone(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && sendOtp()}
              placeholder={t("auth.phonePlaceholder")}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="name">
              {t("auth.nameLabel")} <span className="text-muted-foreground">({t("common.optional")})</span>
            </Label>
            <Input
              id="name"
              data-testid="login-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("auth.namePlaceholder")}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="referral-code">
              {t("auth.referralCodeLabel")} <span className="text-muted-foreground">({t("common.optional")})</span>
            </Label>
            <Input
              id="referral-code"
              data-testid="login-referral-code-input"
              value={referralCode}
              onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
              placeholder={t("auth.referralCodePlaceholder")}
              className="font-mono uppercase tracking-widest"
            />
          </div>
          <Button className="w-full rounded-full" data-testid="send-otp-btn" disabled={loading} onClick={sendOtp}>
            {loading ? t("common.loading") : t("auth.sendOtp")}
          </Button>
          <div className="flex items-center gap-3 text-xs uppercase tracking-widest text-muted-foreground">
            <span className="h-px flex-1 bg-border" />
            {t("auth.or")}
            <span className="h-px flex-1 bg-border" />
          </div>
          <GoogleButton />
        </div>
      ) : (
        <div className="mt-6 space-y-5" data-testid="login-otp-step">
          <h2 className="font-heading text-xl font-semibold">{t("auth.otpTitle")}</h2>
          <p className="text-sm text-muted-foreground">{t("auth.otpSubtitle", { phone: spacedPhone(normalizedPhone) })}</p>
          {devCode && (
            <div
              data-testid="dev-otp-notice"
              className="rounded-xl border border-dashed border-[hsl(var(--accent))]/60 bg-[hsl(var(--accent))]/10 px-4 py-3 text-sm"
            >
              <div className="flex items-center gap-2 font-semibold">
                <AlertTriangle size={16} className="text-[hsl(var(--accent))]" />
                {t("auth.smsPendingTitle")}
              </div>
              <p className="mt-1 text-muted-foreground">{t("auth.smsPendingBody")}</p>
              <p className="mt-2 text-xs uppercase tracking-widest text-muted-foreground">
                {t("auth.smsPendingCodeLabel")}
              </p>
              <p className="font-mono text-3xl font-bold tracking-[0.35em]" data-testid="dev-otp-code">
                {devCode}
              </p>
            </div>
          )}
          <OtpInput
            id="otp"
            testId="login-otp-input"
            errorTestId="login-otp-error"
            label={t("auth.otpLabel")}
            value={code}
            onChange={setCode}
            onEdit={() => setOtpError("")}
            onSubmit={verifyOtp}
            error={otpError}
          />
          <Button className="w-full rounded-full" data-testid="verify-otp-btn" disabled={loading} onClick={verifyOtp}>
            {loading ? t("common.loading") : t("auth.verify")}
          </Button>
          <div className="flex justify-between text-sm">
            <button
              className="flex items-center gap-1 text-muted-foreground"
              data-testid="change-number-btn"
              onClick={() => setStep("phone")}
            >
              <ArrowLeft size={14} /> {t("auth.changeNumber")}
            </button>
            <button
              className="text-[hsl(var(--primary))] disabled:text-muted-foreground"
              data-testid="resend-otp-btn"
              disabled={loading || cooldown > 0}
              onClick={sendOtp}
            >
              {cooldown > 0 ? t("auth.resendIn", { n: cooldown }) : t("auth.resend")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
